import { useEffect, useState } from "react";
import apiClient from "@/services/apiClient";
import DataListPage from "./DataListPage";
import ToastHelper from "@/utils/toastHelper";


const STATUS_STYLES = {
  Active: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Completed: "bg-blue-50 text-blue-700 border border-blue-200",
  Pending: "bg-amber-50 text-amber-700 border border-amber-200",
  Suspended: "bg-rose-50 text-rose-700 border border-rose-200",
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-AU");
};

const normalizeStudents = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.data ?? payload?.result ?? payload?.items ?? payload?.students ?? [];

  return list.map((student, index) => {
    const fullName = [student.firstName, student.lastName]
      .filter(Boolean)
      .join(" ");

    return {
      id: student.id ?? student.studentId ?? student.student_id ?? `S-${index + 1}`,
      name: student.name ?? (fullName || "Unknown"),
      email: student.email ?? student.emailAddress ?? "",
      phone: student.phone ?? student.phoneNumber ?? "",
      usi: student.usiNumber ?? "",
      enrolments: student.enrolments ?? student.enrollmentCount ?? 0,
      status: student.status ?? student.studentStatus ?? "Active",
      joined: formatDate(student.joined ?? student.createdAt ?? student.createdDate),
    };
  });
};

export default function AdminStudents() {
  const [rows, setRows] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await apiClient.get("/AdminStudent/GetStudents");
        setRows(normalizeStudents(response.data));
      } catch (err) {
        console.error("Failed to load students", err);
        setRows([]);
        ToastHelper.apiError(err, "Unable to load students");
      }
    };

    fetchStudents();
  }, []);

  return (<>
    <DataListPage
      title="Students"
      subtitle="Manage all student accounts"
      testidPrefix="students"
      rows={rows}
      statusStyles={STATUS_STYLES}
      actions={[{ label: "View", fn: (student) => setSelectedStudent(student) }]}
      extraFilter={(r, f) => (f === "active" ? r.status === "Active" : f === "completed" ? r.status === "Completed" : true)}
      columns={[
        { key: "id", label: "ID" },
        { key: "name", label: "Name", render: (r) => <span className="font-semibold">{r.name}</span> },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "usi", label: "USI" },
        { key: "enrolments", label: "Enrolments", align: "right" },
        { key: "status", label: "Status", type: "status" },
        { key: "joined", label: "Joined" },
      ]}
    />
    {selectedStudent && <StudentViewDialog student={selectedStudent} onClose={() => setSelectedStudent(null)} />}
  </>);
}

function StudentViewDialog({ student, onClose }) {
  const fields = [["Student ID", student.id], ["Name", student.name], ["Email", student.email], ["Mobile", student.phone], ["USI", student.usi], ["Enrolments", student.enrolments], ["Status", student.status], ["Joined", student.joined]];
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl"><div className="flex items-center justify-between border-b px-6 py-4"><h2 className="text-xl font-display font-extrabold text-[#0A2540]">Student details</h2><button className="text-2xl text-slate-400" onClick={onClose}>×</button></div><div className="grid gap-x-6 gap-y-4 p-6 sm:grid-cols-2">{fields.map(([label, value]) => <div key={label}><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div><div className="mt-1 break-words text-sm text-slate-800">{value || "-"}</div></div>)}</div><div className="flex justify-end border-t px-6 py-4"><button className="wc-btn-primary" onClick={onClose}>Close</button></div></div></div>;
}
